import { useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { Link } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { createChatLink } from "@/lib/appEntryLink"

export function ChatLinkContextMenu({
  jid,
  children,
}: {
  jid?: string
  children: ReactNode
}) {
  const { t } = useTranslation()
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null,
  )
  if (!jid) return <>{children}</>

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(createChatLink(jid))
      toast.success(t("chat.chatLink.copied"))
    } catch {
      toast.error(t("chat.chatLink.copyFailed"))
    }
  }

  return (
    <DropdownMenu
      open={position !== null}
      onOpenChange={(open) => !open && setPosition(null)}
      modal={false}
    >
      <div
        className="contents"
        onContextMenu={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setPosition({ x: event.clientX, y: event.clientY })
        }}
      >
        {children}
      </div>
      {createPortal(
        <DropdownMenuTrigger asChild>
          <span
            aria-hidden
            tabIndex={-1}
            className="pointer-events-none fixed size-px"
            style={{ left: position?.x, top: position?.y }}
          />
        </DropdownMenuTrigger>,
        document.body,
      )}
      <DropdownMenuContent
        align="start"
        sideOffset={0}
        onClick={(event) => event.stopPropagation()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenuItem data-testid="copy-chat-link" onSelect={copyLink}>
          <Link />
          {t("chat.chatLink.copy")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
